import maplibregl from 'maplibre-gl';
import { EventEmitter, type EventHandler, type EventKey } from '../events/MapEvent';
import { MIDiagnostics } from '../diagnostics/MIDiagnostics';
import type { Layer } from '../layers/Layer';
import type { Control } from '../controls/Control';
import type { Interaction } from '../interactions/Interaction';
import type { Extent } from '../geometry/Extent';
import { GISToolBar, type GISToolBarOffset, type GISToolBarOptions } from '../toolbar/GISToolBar';

export interface MIMapOptions {
  target: string | HTMLElement;
  projection?: string;
  center?: [number, number];
  zoom?: number;
  minZoom?: number;
  maxZoom?: number;
  rotation?: number;
  extent?: Extent;
  controls?: Control[];
  interactions?: Interaction[];
  renderer?: 'webgl' | 'canvas2d';
  pixelRatio?: number;
  maxTilesLoading?: number;
  loadTilesWhileInteracting?: boolean;
  moveTolerance?: number;
  enableRotation?: boolean;
  style?: any;
  apiKey?: string;
  apiBaseUrl?: string;
  streamUrl?: string;
}

export interface FitOptions {
  padding?: number | [number, number, number, number];
  maxZoom?: number;
  duration?: number;
}

export interface HitTestOptions {
  layers?: Layer[];
  tolerance?: number;
}

export interface HitTestResult {
  feature: any;
  layer: Layer;
}

export interface ExportOptions {
  format?: 'png' | 'jpeg' | 'webp';
  quality?: number;
}

type MIMapConstructionOptions = MIMapOptions & { __adoptMaplibreMap?: unknown };

function normaliseRawFeatures(data: any): any[] {
  if (!data) return [];
  if (data.type === 'FeatureCollection' && Array.isArray(data.features)) return data.features;
  if (data.type === 'Feature') return [data];
  if (typeof data.type === 'string' && data.coordinates) {
    return [{ type: 'Feature', properties: {}, geometry: data }];
  }
  return [];
}

function adoptedRawLayer(map: maplibregl.Map, spec: any, exposesSourceFeatures: boolean): Layer {
  const sourceData = () => {
    if (!spec.source) return undefined;
    const source: any = map.getSource(spec.source);
    if (!source) return undefined;
    const data = source.data ?? source._data ?? source.serialize?.().data;
    // MapLibre's live GeoJSONSource stores inline data as
    // `{ geojson: data }` (and updateable source data in a Map), neither of
    // which is itself valid GeoJSON for the exchange API.
    if (data?.geojson) return data.geojson;
    if (data?.updateable instanceof Map) {
      return { type: 'FeatureCollection', features: Array.from(data.updateable.values()) };
    }
    return data;
  };
  const source: any = {
    exportSourceId: `maplibre:${spec.source || spec.id}`,
    getFeatures: () => exposesSourceFeatures ? normaliseRawFeatures(sourceData()) : [],
  };
  Object.defineProperty(source, 'data', { enumerable: true, get: sourceData });
  return {
    id: spec.id,
    // This is deliberately a stable source object. Consumers can use
    // exportSourceId to dedupe renderer layers that share one native source.
    exportSourceId: source.exportSourceId,
    getSource: () => source,
    getFeatures: source.getFeatures,
    isVisible: () => {
      try { return map.getLayoutProperty(spec.id, 'visibility') !== 'none'; }
      catch { return false; }
    },
    setVisible: (visible: boolean) => {
      if (map.getLayer(spec.id)) map.setLayoutProperty(spec.id, 'visibility', visible ? 'visible' : 'none');
    },
    _removeFrom: () => { if (map.getLayer(spec.id)) map.removeLayer(spec.id); },
    __adoptedRawLayerId: spec.id,
  } as unknown as Layer;
}

export class MIMap extends EventEmitter {
  // Engine handle is internal — consumers reach it via `unstable_getMaplibreMap()`.
  protected _maplibreMap: maplibregl.Map;
  private _layers: Layer[] = [];
  private _controls: Control[] = [];
  private _interactions: Interaction[] = [];
  private _container: HTMLElement;
  private _loading: boolean = false;
  private _styleLoaded: boolean = false;
  private _pendingApply: Layer[] = [];
  private _gisToolBar: GISToolBar | null = null;
  private _adoptedRawMap: maplibregl.Map | null = null;
  private _ownsMaplibreMap = true;

  private static _globalConfig: {
    apiKey?: string;
    apiBaseUrl?: string;
    streamUrl?: string;
  } = {};

  static configure(config: { apiKey?: string; apiBaseUrl?: string; streamUrl?: string }): void {
    MIMap._globalConfig = config;
  }

  /**
   * Adopt an already-created MapLibre map for an in-product integration.
   * The `unknown` input keeps the engine type out of the public SDK surface.
   * The adopted view never destroys the supplied map; callers retain that
   * lifecycle responsibility.
   */
  static adoptMaplibreMap(map: unknown, target?: HTMLElement): MIMap {
    const nativeMap = map as maplibregl.Map;
    const container = target ?? nativeMap.getContainer?.();
    if (!container) throw new Error('Cannot adopt a MapLibre map without its container.');
    const view = new MIMap({
      target: container,
      __adoptMaplibreMap: nativeMap,
    } as MIMapConstructionOptions);
    view._adoptedRawMap = nativeMap;
    view._syncAdoptedRawLayers();
    return view;
  }

  constructor(options: MIMapConstructionOptions) {
    super();

    const container =
      typeof options.target === 'string'
        ? document.getElementById(options.target)
        : options.target;

    if (!container) throw new Error(`Target element not found: ${options.target}`);
    this._container = container;

    const mapStyle = options.style || {
      version: 8 as const,
      sources: {
        'osm-tiles': {
          type: 'raster' as const,
          tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
          tileSize: 256,
          attribution: '\u00a9 OpenStreetMap contributors',
        },
      },
      layers: [
        {
          id: 'osm-layer',
          type: 'raster' as const,
          source: 'osm-tiles',
          minzoom: 0,
          maxzoom: 19,
        },
      ],
    };

    try {
      if (options.__adoptMaplibreMap) {
        this._maplibreMap = options.__adoptMaplibreMap as maplibregl.Map;
        this._ownsMaplibreMap = false;
        this._styleLoaded = this._maplibreMap.isStyleLoaded() === true;
      } else {
        this._maplibreMap = new maplibregl.Map({
          container: this._container,
          style: mapStyle,
          center: options.center || [0, 0],
          zoom: options.zoom ?? 2,
          minZoom: options.minZoom ?? 0,
          maxZoom: options.maxZoom ?? 22,
          bearing: options.rotation ? (options.rotation * 180) / Math.PI : 0,
          pitchWithRotate: false,
          dragRotate: options.enableRotation !== false,
          pixelRatio: options.pixelRatio || window.devicePixelRatio,
        });
      }
    } catch (error) {
      // MapLibre throws synchronously when its canvas cannot create a WebGL
      // context (notably CI/headless browsers without a software renderer).
      // Do not substitute a fake map: callers need an actionable failure.
      const detail = error instanceof Error ? error.message : String(error);
      throw new Error(
        `Unable to create the MIMap WebGL renderer. Use a WebGL-capable browser ` +
        `(or enable a software WebGL renderer). MapLibre reported: ${detail}`
      );
    }

    this._maplibreMap.on('click', (e) => {
      this.emit('click', {
        coordinate: [e.lngLat.lng, e.lngLat.lat] as [number, number],
        pixel: [e.point.x, e.point.y] as [number, number],
        originalEvent: e.originalEvent,
        dragging: false,
      });
    });

    this._maplibreMap.on('dblclick', (e) => {
      this.emit('dblclick', {
        coordinate: [e.lngLat.lng, e.lngLat.lat] as [number, number],
        pixel: [e.point.x, e.point.y] as [number, number],
        originalEvent: e.originalEvent,
        dragging: false,
      });
    });

    this._maplibreMap.on('contextmenu', (e) => {
      this.emit('contextmenu', {
        coordinate: [e.lngLat.lng, e.lngLat.lat] as [number, number],
        pixel: [e.point.x, e.point.y] as [number, number],
        originalEvent: e.originalEvent,
        dragging: false,
      });
    });

    this._maplibreMap.on('mousemove', (e) => {
      this.emit('pointermove', {
        coordinate: [e.lngLat.lng, e.lngLat.lat] as [number, number],
        pixel: [e.point.x, e.point.y] as [number, number],
        originalEvent: e.originalEvent,
        dragging: false,
      });
    });

    this._maplibreMap.on('movestart', () => this.emit('movestart'));
    this._maplibreMap.on('moveend', () => this.emit('moveend'));
    this._maplibreMap.on('zoomstart', () => this.emit('zoomchange'));
    this._maplibreMap.on('zoomend', () => this.emit('zoomchange'));
    this._maplibreMap.on('rotate', () => this.emit('rotationchange'));
    this._maplibreMap.on('resize', () => this.emit('resize'));

    this._maplibreMap.on('dataloading', () => {
      this._loading = true;
      this.emit('loadstart');
    });

    this._maplibreMap.on('idle', () => {
      this._loading = false;
      this.emit('loadend');
    });

    this._maplibreMap.on('error', (e) => {
      this.emit('error', { error: e.error });
    });

    this._maplibreMap.on('load', () => {
      this._styleLoaded = true;
      // Flush in canonical _layers order so beforeId resolves correctly between
      // queued layers (instead of replaying insertion order without context).
      this._pendingApply = [];
      for (let i = 0; i < this._layers.length; i++) {
        const lyr = this._layers[i];
        if ((lyr as any)._appliedMap) continue;
        const beforeId = this._beforeIdForIndex(i);
        try { lyr._applyTo(this._maplibreMap, beforeId); } catch (err) {
          this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
        }
      }
      this.emit('load');
    });

    this._maplibreMap.on('styledata', () => {
      if (this._maplibreMap.isStyleLoaded()) this._styleLoaded = true;
    });

    if (options.controls) {
      options.controls.forEach((c) => this.addControl(c));
    }

    if (options.interactions) {
      options.interactions.forEach((i) => this.addInteraction(i));
    }
  }

  get layers(): Layer[] {
    this._syncAdoptedRawLayers();
    return [...this._layers];
  }

  get controls(): Control[] {
    return [...this._controls];
  }

  get interactions(): Interaction[] {
    return [...this._interactions];
  }

  get viewport(): HTMLElement {
    return this._container;
  }

  get canvas(): HTMLCanvasElement {
    return this._maplibreMap.getCanvas();
  }

  get size(): [number, number] {
    const el = this._container;
    return [el.clientWidth, el.clientHeight];
  }

  get loading(): boolean {
    return this._loading;
  }

  addLayer(layer: Layer, index?: number): void {
    if (index !== undefined) {
      this._layers.splice(index, 0, layer);
    } else {
      this._layers.push(layer);
    }

    const beforeId = this._beforeIdForIndex(index);
    if (this._styleLoaded) {
      try { layer._applyTo(this._maplibreMap, beforeId); }
      catch (err) { this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) }); }
    } else {
      this._pendingApply.push(layer);
    }
    this.emit('layeradd', { layer });
  }

  private _beforeIdForIndex(index?: number): string | undefined {
    if (index === undefined) return undefined;
    const next = this._layers[index + 1];
    if (!next) return undefined;
    const ids = (next as any)._managedLayerIds as string[] | undefined;
    return ids && ids.length ? ids[0] : undefined;
  }

  removeLayer(layer: Layer): Layer | undefined {
    const idx = this._layers.indexOf(layer);
    if (idx >= 0) {
      this._layers.splice(idx, 1);
      const pendIdx = this._pendingApply.indexOf(layer);
      if (pendIdx >= 0) this._pendingApply.splice(pendIdx, 1);
      try { layer._removeFrom(this._maplibreMap); } catch {}
      this.emit('layerremove', { layer });
      return layer;
    }
    return undefined;
  }

  getLayers(): Layer[] {
    this._syncAdoptedRawLayers();
    return [...this._layers];
  }

  /** Keep an adopted raw map's live style layers visible to toolbar panels/export. */
  private _syncAdoptedRawLayers(): void {
    const raw = this._adoptedRawMap;
    if (!raw) return;
    // SDK Layer renderers add MapLibre style layers too. They are already in
    // _layers and must not be reintroduced as raw wrappers.
    const managedRendererIds = new Set<string>();
    this._layers.forEach((layer: any) => {
      (layer._managedLayerIds || []).forEach((id: string) => managedRendererIds.add(id));
    });
    const specs = (raw.getStyle()?.layers || []).filter((spec: any) => !managedRendererIds.has(spec.id));
    const ids = new Set(specs.map((spec: any) => spec.id));
    this._layers = this._layers.filter((layer: any) => !layer.__adoptedRawLayerId || ids.has(layer.__adoptedRawLayerId));
    const featureSourceIds = new Set<string>();
    specs.forEach((spec: any) => {
      const sourceId = spec.source ? `maplibre:${spec.source}` : `maplibre:${spec.id}`;
      const exposesSourceFeatures = !featureSourceIds.has(sourceId);
      featureSourceIds.add(sourceId);
      if (!this._layers.some((layer: any) => layer.__adoptedRawLayerId === spec.id)) {
        this._layers.push(adoptedRawLayer(raw, spec, exposesSourceFeatures));
      }
    });
  }

  getLayerById(id: string): Layer | undefined {
    return this._layers.find((l) => l.id === id);
  }

  getLayersByType(type: string): Layer[] {
    return this._layers.filter((l) => l.constructor.name.toLowerCase().includes(type));
  }

  reorderLayer(layer: Layer, index: number): void {
    const idx = this._layers.indexOf(layer);
    if (idx < 0) return;
    this._layers.splice(idx, 1);
    this._layers.splice(index, 0, layer);
    if (!this._styleLoaded) return;

    const beforeId = this._beforeIdForIndex(index);
    const ids = (layer as any)._managedLayerIds as string[];
    for (const lid of ids) {
      try { this._maplibreMap.moveLayer(lid, beforeId); } catch {}
    }
  }

  clearLayers(): void {
    for (const lyr of this._layers) {
      try { lyr._removeFrom(this._maplibreMap); } catch {}
    }
    this._layers = [];
    this._pendingApply = [];
  }

  isStyleLoaded(): boolean {
    return this._styleLoaded;
  }

  setCenter(center: [number, number], animate: boolean = false): void {
    if (animate) {
      this._maplibreMap.panTo(center, { duration: 500 });
    } else {
      this._maplibreMap.setCenter(center);
    }
  }

  getCenter(): [number, number] {
    const c = this._maplibreMap.getCenter();
    return [c.lng, c.lat];
  }

  setZoom(zoom: number, animate: boolean = false): void {
    if (animate) {
      this._maplibreMap.zoomTo(zoom, { duration: 500 });
    } else {
      this._maplibreMap.setZoom(zoom);
    }
  }

  getZoom(): number {
    return this._maplibreMap.getZoom();
  }

  setRotation(rotation: number, animate: boolean = false): void {
    const bearing = (rotation * 180) / Math.PI;
    if (animate) {
      this._maplibreMap.rotateTo(bearing, { duration: 500 });
    } else {
      this._maplibreMap.setBearing(bearing);
    }
  }

  getRotation(): number {
    return (this._maplibreMap.getBearing() * Math.PI) / 180;
  }

  setPitch(pitch: number): void {
    this._maplibreMap.setPitch(pitch);
  }

  getPitch(): number {
    return this._maplibreMap.getPitch();
  }

  flyTo(options: {
    center?: [number, number];
    zoom?: number;
    bearing?: number;
    pitch?: number;
    duration?: number;
  }): void {
    this._maplibreMap.flyTo({
      center: options.center,
      zoom: options.zoom,
      bearing: options.bearing,
      pitch: options.pitch,
      duration: options.duration ?? 1000,
    } as any);
  }

  easeTo(options: {
    center?: [number, number];
    zoom?: number;
    bearing?: number;
    pitch?: number;
    duration?: number;
  }): void {
    this._maplibreMap.easeTo({
      center: options.center,
      zoom: options.zoom,
      bearing: options.bearing,
      pitch: options.pitch,
      duration: options.duration ?? 1000,
    } as any);
  }

  whenReady(callback: () => void): void {
    if (this._styleLoaded) callback();
    else this.once('load', callback);
  }

  getScale(): number {
    const center = this._maplibreMap.getCenter();
    const metersPerPixel =
      (40075016.686 * Math.cos((center.lat * Math.PI) / 180)) /
      (Math.pow(2, this._maplibreMap.getZoom() + 8));
    return metersPerPixel * 96 * 39.37;
  }

  getResolution(): number {
    const center = this._maplibreMap.getCenter();
    return (
      (40075016.686 * Math.cos((center.lat * Math.PI) / 180)) /
      Math.pow(2, this._maplibreMap.getZoom() + 8)
    );
  }

  getExtent(): { minX: number; minY: number; maxX: number; maxY: number } {
    const bounds = this._maplibreMap.getBounds();
    return {
      minX: bounds.getWest(),
      minY: bounds.getSouth(),
      maxX: bounds.getEast(),
      maxY: bounds.getNorth(),
    };
  }

  fitExtent(
    extent: { minX: number; minY: number; maxX: number; maxY: number },
    options?: FitOptions
  ): void {
    const padding = typeof options?.padding === 'number'
      ? options.padding
      : options?.padding
        ? Math.max(...options.padding)
        : 50;

    this._maplibreMap.fitBounds(
      [
        [extent.minX, extent.minY],
        [extent.maxX, extent.maxY],
      ],
      {
        padding,
        maxZoom: options?.maxZoom,
        duration: options?.duration ?? 500,
      }
    );
  }

  toMapCoordinate(pixel: [number, number]): [number, number] {
    const lngLat = this._maplibreMap.unproject(pixel);
    return [lngLat.lng, lngLat.lat];
  }

  toPixelCoordinate(coord: [number, number]): [number, number] {
    const point = this._maplibreMap.project(coord as [number, number]);
    return [point.x, point.y];
  }

  getProjection(): string {
    try {
      const p: any = (this._maplibreMap as any).getProjection?.();
      if (p) {
        if (typeof p === 'string') return p;
        if (typeof p.name === 'string') return p.name;
        if (typeof p.type === 'string') return p.type;
      }
    } catch {}
    return 'mercator';
  }

  addControl(control: Control): void {
    this._controls.push(control);
    control.setMap(this);
    // Prefer the MapLibre IControl adapter path so controls are real children of
    // the map's control container (correct positioning, themability, focus).
    const adapter = (control as any).getMaplibreAdapter?.();
    if (adapter) {
      try { this._maplibreMap.addControl(adapter, control.getPosition()); } catch {}
    } else {
      control.render();
    }
  }

  removeControl(control: Control): void {
    const idx = this._controls.indexOf(control);
    if (idx >= 0) {
      this._controls.splice(idx, 1);
      const adapter = (control as any)._attachedMaplibreControl;
      if (adapter) { try { this._maplibreMap.removeControl(adapter); } catch {} }
      control.destroy();
    }
  }

  addInteraction(interaction: Interaction): void {
    this._interactions.push(interaction);
    interaction.setMap(this);
  }

  removeInteraction(interaction: Interaction): void {
    const idx = this._interactions.indexOf(interaction);
    if (idx >= 0) this._interactions.splice(idx, 1);
  }

  async exportImage(options?: ExportOptions): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        const canvas = this._maplibreMap.getCanvas();
        const format = options?.format === 'jpeg' ? 'image/jpeg' : 'image/png';
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to export image'));
          },
          format,
          options?.quality || 0.92
        );
      } catch (e) {
        reject(e);
      }
    });
  }

  updateSize(): void {
    this._maplibreMap.resize();
  }

  render(): void {
    this._maplibreMap.triggerRepaint();
  }

  /**
   * Show the standalone MIGIS GIS toolbar over this map. This is a DOM widget,
   * not a React component. Calling it repeatedly updates the existing widget.
   */
  showGISToolBar(options?: GISToolBarOptions): GISToolBar;
  /** Legacy positional form: `showGISToolBar([x, y], true)`. */
  showGISToolBar(offset?: GISToolBarOffset, draggable?: boolean): GISToolBar;
  showGISToolBar(
    optionsOrOffset: GISToolBarOptions | GISToolBarOffset = {},
    draggable?: boolean
  ): GISToolBar {
    const options: GISToolBarOptions = Array.isArray(optionsOrOffset)
      ? { offset: optionsOrOffset, draggable }
      : ('x' in optionsOrOffset && 'y' in optionsOrOffset)
        ? { offset: optionsOrOffset, draggable }
        : optionsOrOffset;
    if (this._gisToolBar) {
      this._gisToolBar.update(options);
      return this._gisToolBar;
    }
    this._gisToolBar = new GISToolBar(this, options);
    return this._gisToolBar;
  }

  /** Remove the GIS toolbar and cancel any drawing/measurement it owns. */
  hideGISToolBar(): void {
    this._gisToolBar?.destroy();
    this._gisToolBar = null;
  }

  destroy(): void {
    this.hideGISToolBar();
    this._controls.forEach((c) => {
      try { c.destroy(); } catch {}
    });
    // Tear down any layer-scoped event bridges so they don't outlive the map.
    for (const [, bridges] of this._scopedBridges) {
      for (const b of bridges) {
        try { (this._maplibreMap as any).off(b.event, b.layerId, b.native); } catch {}
      }
    }
    this._scopedBridges.clear();
    if (this._ownsMaplibreMap) {
      try { this._maplibreMap.remove(); } catch {}
    }
    this.removeAllListeners();
  }

  // ─── Layer-scoped event bridging (Step 3) ─────────────────────────────
  // When a caller does mimap.on('click', 'roads', handler), forward the
  // subscription to the underlying MapLibre map so it dispatches only when
  // the cursor intersects features in that engine layer.
  // Allow the same handler to be registered under multiple (event, layerId)
  // pairs. Each registration gets its own bridge entry so off() can remove
  // exactly the matching one without disturbing the others.
  private _scopedBridges: Map<EventHandler, Array<{ event: string; layerId: string; native: any }>> = new Map();

  private _wrapMaplibrePointerEvent(e: any, layerId: string): any {
    return {
      type: e.type,
      target: this,
      coordinate: e?.lngLat ? [e.lngLat.lng, e.lngLat.lat] as [number, number] : undefined,
      pixel: e?.point ? [e.point.x, e.point.y] as [number, number] : undefined,
      originalEvent: e?.originalEvent,
      dragging: false,
      layer: { id: layerId },
      features: Array.isArray(e?.features) ? e.features : [],
    };
  }

  private _registerScopedBridge(handler: EventHandler, entry: { event: string; layerId: string; native: any }): void {
    const existing = this._scopedBridges.get(handler) ?? [];
    existing.push(entry);
    this._scopedBridges.set(handler, existing);
  }

  private _removeScopedBridge(handler: EventHandler, event: string, layerId: string): { event: string; layerId: string; native: any } | null {
    const list = this._scopedBridges.get(handler);
    if (!list) return null;
    const idx = list.findIndex(b => b.event === event && b.layerId === layerId);
    if (idx < 0) return null;
    const [removed] = list.splice(idx, 1);
    if (list.length === 0) this._scopedBridges.delete(handler);
    return removed;
  }

  on(event: string, layerIdOrHandler: string | EventHandler, maybeHandler?: EventHandler): EventKey {
    if (typeof layerIdOrHandler === 'string' && typeof maybeHandler === 'function') {
      const layerId = layerIdOrHandler;
      const handler = maybeHandler;
      const native = (e: any) => handler(this._wrapMaplibrePointerEvent(e, layerId));
      this._registerScopedBridge(handler, { event, layerId, native });
      try { (this._maplibreMap as any).on(event, layerId, native); } catch {}
      return { type: event, handler, layerId };
    }
    return super.on(event, layerIdOrHandler as EventHandler);
  }

  off(event: string, layerIdOrHandler: string | EventHandler, maybeHandler?: EventHandler): void {
    if (typeof layerIdOrHandler === 'string' && typeof maybeHandler === 'function') {
      const layerId = layerIdOrHandler;
      const handler = maybeHandler;
      const bridge = this._removeScopedBridge(handler, event, layerId);
      if (bridge) {
        try { (this._maplibreMap as any).off(bridge.event, bridge.layerId, bridge.native); } catch {}
      }
      return;
    }
    super.off(event, layerIdOrHandler as EventHandler);
  }

  once(event: string, layerIdOrHandler: string | EventHandler, maybeHandler?: EventHandler): EventKey {
    if (typeof layerIdOrHandler === 'string' && typeof maybeHandler === 'function') {
      const layerId = layerIdOrHandler;
      const handler = maybeHandler;
      const native = (e: any) => {
        try { (this._maplibreMap as any).off(event, layerId, native); } catch {}
        this._removeScopedBridge(handler, event, layerId);
        handler(this._wrapMaplibrePointerEvent(e, layerId));
      };
      this._registerScopedBridge(handler, { event, layerId, native });
      try { (this._maplibreMap as any).once(event, layerId, native); } catch {}
      return { type: event, handler, layerId };
    }
    return super.once(event, layerIdOrHandler as EventHandler);
  }

  // ─── Camera / view-control parity (Step 1) ────────────────────────────
  panBy(offset: [number, number], options?: { duration?: number }): void {
    this._maplibreMap.panBy(offset as any, options as any);
  }
  jumpTo(opts: { center?: [number, number]; zoom?: number; bearing?: number; pitch?: number }): void {
    this._maplibreMap.jumpTo(opts as any);
  }
  zoomIn(options?: { duration?: number }): void { this._maplibreMap.zoomIn(options as any); }
  zoomOut(options?: { duration?: number }): void { this._maplibreMap.zoomOut(options as any); }
  rotateTo(bearingDeg: number, options?: { duration?: number }): void {
    this._maplibreMap.rotateTo(bearingDeg, options as any);
  }
  resetNorth(options?: { duration?: number }): void { this._maplibreMap.resetNorth(options as any); }
  snapToNorth(options?: { duration?: number }): void { this._maplibreMap.snapToNorth(options as any); }
  stop(): void { this._maplibreMap.stop(); }
  isMoving(): boolean { return this._maplibreMap.isMoving(); }
  isZooming(): boolean { return this._maplibreMap.isZooming(); }
  isRotating(): boolean { return this._maplibreMap.isRotating(); }
  cameraForBounds(
    extent: { minX: number; minY: number; maxX: number; maxY: number },
    options?: { padding?: number; bearing?: number; pitch?: number }
  ): { center: [number, number]; zoom: number; bearing: number } | null {
    try {
      const cam: any = (this._maplibreMap as any).cameraForBounds(
        [[extent.minX, extent.minY], [extent.maxX, extent.maxY]],
        options as any
      );
      if (!cam) return null;
      return { center: [cam.center.lng, cam.center.lat], zoom: cam.zoom, bearing: cam.bearing };
    } catch { return null; }
  }
  fitScreenCoordinates(
    p0: [number, number], p1: [number, number], bearing: number, options?: FitOptions
  ): void {
    (this._maplibreMap as any).fitScreenCoordinates(p0, p1, bearing, options as any);
  }
  setMinZoom(z: number | null): void { this._maplibreMap.setMinZoom(z as any); }
  setMaxZoom(z: number | null): void { this._maplibreMap.setMaxZoom(z as any); }
  setMinPitch(p: number | null): void { this._maplibreMap.setMinPitch(p as any); }
  setMaxPitch(p: number | null): void { this._maplibreMap.setMaxPitch(p as any); }
  setMaxBounds(extent: { minX: number; minY: number; maxX: number; maxY: number } | null): void {
    if (!extent) { this._maplibreMap.setMaxBounds(null as any); return; }
    this._maplibreMap.setMaxBounds([[extent.minX, extent.minY], [extent.maxX, extent.maxY]] as any);
  }
  setPadding(padding: { top?: number; bottom?: number; left?: number; right?: number }): void {
    (this._maplibreMap as any).setPadding(padding);
  }
  getPadding(): { top: number; bottom: number; left: number; right: number } {
    return (this._maplibreMap as any).getPadding?.() ?? { top: 0, bottom: 0, left: 0, right: 0 };
  }
  setRenderWorldCopies(v: boolean): void { (this._maplibreMap as any).setRenderWorldCopies?.(v); }
  setFreeCameraOptions(opts: any): void { (this._maplibreMap as any).setFreeCameraOptions?.(opts); }
  getFreeCameraOptions(): any { return (this._maplibreMap as any).getFreeCameraOptions?.(); }

  // ─── Style + source mutation parity (Step 2) ──────────────────────────
  setStyle(style: any, options?: { diff?: boolean }): void {
    this._styleLoaded = false;
    this._maplibreMap.setStyle(style as any, options as any);
  }
  getStyle(): any { return this._maplibreMap.getStyle(); }
  setProjection(projection: 'mercator' | 'globe' | any): void {
    (this._maplibreMap as any).setProjection?.(typeof projection === 'string' ? { type: projection } : projection);
  }
  setSky(sky: any): void { (this._maplibreMap as any).setSky?.(sky); }
  setFog(fog: any): void { (this._maplibreMap as any).setFog?.(fog); }
  setLight(light: any): void { (this._maplibreMap as any).setLight?.(light); }
  /**
   * Configure raster-DEM terrain on the 2D map (MapLibre `setTerrain`).
   * Pass `null` to disable.
   */
  setTerrain(terrain: { source: string; exaggeration?: number } | null): void {
    (this._maplibreMap as any).setTerrain?.(terrain as any);
  }
  /** @deprecated Use {@link MIMap.setTerrain} instead. Kept for backward compatibility. */
  setMaplibreTerrain(terrain: { source: string; exaggeration?: number } | null): void {
    this.setTerrain(terrain);
  }
  /** Returns the active terrain spec, or null if none. */
  getTerrain(): { source: string; exaggeration?: number } | null {
    return ((this._maplibreMap as any).getTerrain?.() ?? null) as any;
  }
  setLocale(locale: Record<string, string>): void { (this._maplibreMap as any).setLocale?.(locale); }
  addImage(id: string, image: any, options?: any): void { this._maplibreMap.addImage(id, image, options); }
  removeImage(id: string): void { this._maplibreMap.removeImage(id); }
  hasImage(id: string): boolean { return this._maplibreMap.hasImage(id); }
  loadImage(url: string): Promise<any> { return (this._maplibreMap as any).loadImage(url); }
  updateImage(id: string, image: any): void { (this._maplibreMap as any).updateImage?.(id, image); }
  listImages(): string[] { return (this._maplibreMap as any).listImages?.() ?? []; }
  addSprite(id: string, url: string): void { (this._maplibreMap as any).addSprite?.(id, url); }
  removeSprite(id: string): void { (this._maplibreMap as any).removeSprite?.(id); }
  // By-id layer mutators (operate on engine layer ids — typically `<MILayer.id>__<sub>`)
  setPaintProperty(layerId: string, name: string, value: any): void {
    this._maplibreMap.setPaintProperty(layerId, name, value);
  }
  setLayoutProperty(layerId: string, name: string, value: any): void {
    this._maplibreMap.setLayoutProperty(layerId, name, value);
  }
  setFilter(layerId: string, filter: any): void { this._maplibreMap.setFilter(layerId, filter); }
  setLayerZoomRange(layerId: string, minZoom: number, maxZoom: number): void {
    this._maplibreMap.setLayerZoomRange(layerId, minZoom, maxZoom);
  }
  moveLayer(layerId: string, beforeId?: string): void { this._maplibreMap.moveLayer(layerId, beforeId); }
  getLayer(layerId: string): any { return this._maplibreMap.getLayer(layerId); }
  getPaintProperty(layerId: string, name: string): any { return this._maplibreMap.getPaintProperty(layerId, name); }
  getLayoutProperty(layerId: string, name: string): any { return this._maplibreMap.getLayoutProperty(layerId, name); }
  getFilter(layerId: string): any { return this._maplibreMap.getFilter(layerId); }
  // Raw source CRUD (advanced — most users should use addLayer with an MISource)
  addSource(id: string, source: any): void { this._maplibreMap.addSource(id, source); }
  removeSource(id: string): void { this._maplibreMap.removeSource(id); }
  getSource(id: string): any { return this._maplibreMap.getSource(id); }
  isSourceLoaded(id: string): boolean { return this._maplibreMap.isSourceLoaded(id); }
  areTilesLoaded(): boolean { return this._maplibreMap.areTilesLoaded(); }

  // ─── Feature querying + scoped events (Step 3) ────────────────────────
  queryRenderedFeatures(
    geometry?: [number, number] | [[number, number], [number, number]],
    options?: { layers?: string[]; filter?: any[] }
  ): any[] {
    try { return this._maplibreMap.queryRenderedFeatures(geometry as any, options as any) ?? []; }
    catch { return []; }
  }
  querySourceFeatures(sourceId: string, options?: { sourceLayer?: string; filter?: any[] }): any[] {
    try { return this._maplibreMap.querySourceFeatures(sourceId, options as any) ?? []; }
    catch { return []; }
  }
  setFeatureState(target: { source: string; sourceLayer?: string; id: string | number }, state: Record<string, any>): void {
    this._maplibreMap.setFeatureState(target as any, state);
  }
  getFeatureState(target: { source: string; sourceLayer?: string; id: string | number }): Record<string, any> {
    return this._maplibreMap.getFeatureState(target as any);
  }
  removeFeatureState(target: { source: string; sourceLayer?: string; id?: string | number }, key?: string): void {
    (this._maplibreMap as any).removeFeatureState(target, key);
  }

  // ─── Diagnostics + container helpers (Step 4) ─────────────────────────
  showTileBoundaries(v: boolean): void { (this._maplibreMap as any).showTileBoundaries = v; }
  showCollisionBoxes(v: boolean): void { (this._maplibreMap as any).showCollisionBoxes = v; }
  showPadding(v: boolean): void { (this._maplibreMap as any).showPadding = v; }
  showOverdrawInspector(v: boolean): void { (this._maplibreMap as any).showOverdrawInspector = v; }
  repaint(v: boolean): void { (this._maplibreMap as any).repaint = v; }
  loaded(): boolean { return this._maplibreMap.loaded(); }
  hasControl(control: Control): boolean { return this._controls.includes(control); }
  getContainer(): HTMLElement { return this._container; }
  getCanvasContainer(): HTMLElement { return this._maplibreMap.getCanvasContainer(); }

  // ─── Engine escape hatches (Step 11) ──────────────────────────────────
  /** Escape hatch — direct MapLibre access. Use only when MIGIS lacks coverage. */
  unstable_getMaplibreMap(): maplibregl.Map { return this._maplibreMap; }

  /**
   * Escape hatch — returns the entire `maplibregl` namespace (`Marker`,
   * `LngLatBounds`, `NavigationControl`, etc.). Use ONLY for ancillary
   * helper classes the SDK does not yet wrap. Emits a one-time
   * MIDiagnostics warning per process.
   */
  static unstable_getMaplibreNamespace(): typeof maplibregl {
    if (!MIMap._deprecationWarned.has('unstable_getMaplibreNamespace')) {
      MIMap._deprecationWarned.add('unstable_getMaplibreNamespace');
      MIDiagnostics.warn(
        'MIMap',
        'unstable_getMaplibreNamespace() is an escape hatch — prefer SDK wrappers when available'
      );
    }
    return maplibregl;
  }

  /**
   * @deprecated Use {@link unstable_getMaplibreMap} instead. The return type is
   * intentionally `unknown` here so the deprecated alias does not surface a
   * `maplibregl.*` type on the public API; consumers willing to take the
   * engine dependency should call the sanctioned escape hatch.
   */
  getMaplibreMap(): unknown {
    if (!MIMap._deprecationWarned.has('getMaplibreMap')) {
      MIMap._deprecationWarned.add('getMaplibreMap');
      MIDiagnostics.warn('MIMap', 'getMaplibreMap() is deprecated; use unstable_getMaplibreMap()');
    }
    return this._maplibreMap;
  }

  private static _deprecationWarned: Set<string> = new Set();
}
